import type { Course, LngLat } from './types.ts';
import { distanceM } from './geo.ts';

/**
 * Which hole is the golfer on?
 *
 * Nearest-hole-wins is wrong. Golf courses run fairways side by side, often in
 * opposite directions, and a ball hit into the trees can put you closer to the
 * 7th green than the 4th you are actually playing. Naive nearest-hole flickers
 * between them, and a yardage that changes hole mid-shot is worse than no app.
 *
 * A round has structure, and this uses all of it:
 *
 *   1. SEQUENCE. You play 1, then 2, then 3. The next hole is enormously more
 *      likely than any other, so it gets a large prior. Going backwards is
 *      possible (a lost ball, a replayed hole) but rare.
 *   2. GREEN THEN TEE. You reach a tee having just left the previous green.
 *      Once the green of the current hole has been visited, advancing becomes
 *      much cheaper.
 *   3. SUSTAINED EVIDENCE. A single fix never switches holes. A candidate must
 *      stay ahead for a set time before it wins, which kills flicker from GPS
 *      noise and from walking past an adjacent tee.
 *   4. MANUAL WINS. If the golfer picks a hole, that holds for a while. They
 *      know where they are; the algorithm is a convenience.
 *
 * Corridor distance — distance to the tee→green line, not to the tee or green —
 * is used throughout, because a golfer spends most of a hole in the middle of
 * it, nowhere near either end.
 */

export interface DetectorConfig {
  /** A candidate must lead for this long before the hole switches. */
  sustainMs: number;
  /** Candidate must beat the current hole's score by this margin (metres). */
  marginM: number;
  /** How long a manual choice suppresses auto-detection. */
  manualHoldMs: number;
  /** Within this distance of the green, count it as visited. */
  greenRadiusM: number;
  /** Fixes worse than this never move the hole. */
  maxAccuracyM: number;
}

export const DEFAULTS: DetectorConfig = {
  sustainMs: 20_000,
  marginM: 45,
  manualHoldMs: 180_000,
  greenRadiusM: 40,
  maxAccuracyM: 25,
};

export interface DetectorState {
  hole: number;
  /** 0-1. Low confidence is surfaced in the UI rather than hidden. */
  confidence: number;
  visitedGreen: boolean;
  candidate: number | null;
  candidateSince: number | null;
  manualUntil: number | null;
  lastSwitchAt: number | null;
}

export function initialState(hole = 1): DetectorState {
  return {
    hole,
    confidence: 0.5,
    visitedGreen: false,
    candidate: null,
    candidateSince: null,
    manualUntil: null,
    lastSwitchAt: null,
  };
}

/** Perpendicular distance to the tee→green corridor, clamped to the segment. */
export function corridorDistance(p: LngLat, tee: LngLat, green: LngLat): number {
  const dx = green[0] - tee[0];
  const dy = green[1] - tee[1];
  const len2 = dx * dx + dy * dy;
  if (len2 < 1e-14) return distanceM(p, tee);
  let t = ((p[0] - tee[0]) * dx + (p[1] - tee[1]) * dy) / len2;
  t = Math.max(0, Math.min(1, t));
  return distanceM(p, [tee[0] + t * dx, tee[1] + t * dy]);
}

function teeOf(course: Course, index: number, teeColour: string): LngLat {
  const h = course.holes[index];
  return h.tees[teeColour] ?? h.tees.White ?? h.green.middle;
}

/**
 * Raw geometric cost for each hole, before priors. Lower is better.
 */
function costs(course: Course, pos: LngLat, teeColour: string): number[] {
  return course.holes.map((h, i) =>
    corridorDistance(pos, teeOf(course, i, teeColour), h.green.middle)
  );
}

export interface DetectorInput {
  course: Course;
  pos: LngLat;
  accuracy: number;
  teeColour: string;
  now: number;
}

/**
 * Advance the detector by one GPS fix. Pure — state in, state out — so the whole
 * thing is testable by replaying a round without a device.
 */
export function step(state: DetectorState, input: DetectorInput): DetectorState {
  const { course, pos, accuracy, teeColour, now } = input;
  if (!course.holes.length) return state;

  // Junk fixes must never move the hole, but they should not reset progress.
  if (accuracy > DEFAULTS.maxAccuracyM) return state;

  const idx = Math.max(0, course.holes.findIndex((h) => h.number === state.hole));
  const current = course.holes[idx] ?? course.holes[0];

  // Track whether the current green has been reached — that is the signal that
  // the hole is finishing and the next one is imminent.
  const nearGreen = distanceM(pos, current.green.middle) <= DEFAULTS.greenRadiusM;
  const visitedGreen = state.visitedGreen || nearGreen;

  if (state.manualUntil && now < state.manualUntil) {
    return { ...state, visitedGreen, candidate: null, candidateSince: null };
  }

  const raw = costs(course, pos, teeColour);

  // Priors, expressed in metres so they combine with corridor distance directly.
  const nextIdx = (idx + 1) % course.holes.length;
  const prevIdx = (idx - 1 + course.holes.length) % course.holes.length;
  const adjusted = raw.map((c, i) => {
    if (i === idx) return c;
    if (i === nextIdx) {
      // Only once the green has been reached does advancing make sense. Before
      // that the golfer is still playing this hole, and a drive sprayed onto
      // the next fairway must not drag the yardage forward — so the next hole
      // is mildly penalised, not favoured.
      return c + (visitedGreen ? -90 : 10);
    }
    // Replaying a hole happens (lost ball, a second go). Resisted, not blocked:
    // the sustain window already filters out brief backtracks.
    if (i === prevIdx) return c + 15;
    // Any other hole has to be decisively closer to win.
    return c + 120;
  });

  let bestIdx = 0;
  for (let i = 1; i < adjusted.length; i++) if (adjusted[i] < adjusted[bestIdx]) bestIdx = i;

  const currentCost = adjusted[idx];
  const bestCost = adjusted[bestIdx];

  // Confidence: how decisively the winner beats the runner-up.
  const sorted = [...adjusted].sort((a, b) => a - b);
  const gap = (sorted[1] ?? sorted[0]) - sorted[0];
  const confidence = Math.max(0.05, Math.min(1, gap / 120));

  if (bestIdx === idx || bestCost > currentCost - DEFAULTS.marginM) {
    // Nothing convincingly better — stay put and drop any candidate.
    return {
      ...state,
      visitedGreen,
      confidence,
      candidate: null,
      candidateSince: null,
    };
  }

  const candidateHole = course.holes[bestIdx].number;

  if (state.candidate !== candidateHole) {
    return {
      ...state,
      visitedGreen,
      confidence,
      candidate: candidateHole,
      candidateSince: now,
    };
  }

  const heldFor = now - (state.candidateSince ?? now);
  if (heldFor < DEFAULTS.sustainMs) {
    return { ...state, visitedGreen, confidence };
  }

  // Candidate has led convincingly for long enough. Switch.
  return {
    hole: candidateHole,
    confidence,
    visitedGreen: false,
    candidate: null,
    candidateSince: null,
    manualUntil: null,
    lastSwitchAt: now,
  };
}

/** The golfer picked a hole. Their choice beats the algorithm for a while. */
export function setManual(state: DetectorState, hole: number, now: number): DetectorState {
  return {
    ...state,
    hole,
    confidence: 1,
    visitedGreen: false,
    candidate: null,
    candidateSince: null,
    manualUntil: now + DEFAULTS.manualHoldMs,
    lastSwitchAt: now,
  };
}
